import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { addWsListener, connectWs } from "../services/wsClient";

const ALL_AUDIENCES = ["Owner", "Tenant", "Executive"];
const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "scheduled", label: "Scheduled" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" }
];
const SORT_FIELDS = [
  { value: "createdAt", label: "Created" },
  { value: "question", label: "Question" },
  { value: "audience", label: "Audience" },
  { value: "endTime", label: "End time" }
];

function isApiSuccess(data) {
  return data?.success === true || data?.status === "success";
}

function pickPollList(data) {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.polls)) return data.polls;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.data?.polls)) return data.data.polls;
  return [];
}

function getPollId(row) {
  return String(row?._id || row?.id || row?.pollId || "").trim();
}

function formatDateTime(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function displayPollStatus(status, endTime, startTime) {
  const s = String(status || "active").toLowerCase().trim();
  if (s === "ended" || s === "closed") return "ended";
  if (s === "scheduled") {
    // If start time has already passed, treat as active
    if (startTime) {
      const start = new Date(startTime).getTime();
      if (!Number.isNaN(start) && start <= Date.now()) return "active";
    }
    return "scheduled";
  }
  if (endTime) {
    const end = new Date(endTime).getTime();
    if (!Number.isNaN(end) && end <= Date.now()) return "ended";
  }
  // Check if start time is in the future (status not yet set to scheduled by server)
  if (startTime) {
    const start = new Date(startTime).getTime();
    if (!Number.isNaN(start) && start > Date.now()) return "scheduled";
  }
  return "active";
}

function statusBadgeStyle(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "open") {
    return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  }
  if (s === "ended" || s === "closed") {
    return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
  }
  if (s === "scheduled") {
    return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
  }
  return { background: "rgba(107,114,128,0.15)", color: "#4b5563" };
}

function statusLabel(status, startTime) {
  if (status === "scheduled") return "Upcoming";
  return status.toUpperCase();
}

function audienceBadgeStyle(audience) {
  const a = String(audience || "").toLowerCase();
  if (a === "owner") return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
  if (a === "tenant") return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
  if (a === "executive") return { background: "rgba(124,58,237,0.15)", color: "#7c3aed" };
  return { background: "rgba(14,165,233,0.15)", color: "#0284c7" };
}

function EVoting() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
  const userId = useMemo(() => localStorage.getItem("userid") || "", []);
  const role = useMemo(
    () =>
      String(localStorage.getItem("role") || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, ""),
    []
  );
  const userRole = useMemo(
    () => String(localStorage.getItem("role") || "user").trim() || "user",
    []
  );
  const isAdmin = role === "ADMIN" || role === "SUPERADMIN";
  const isOwner = role === "OWNER";
  const isTenant = role === "TENANT";

  const [isActiveExecutive, setIsActiveExecutive] = useState(false);
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [audience, setAudience] = useState("");
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, question }
  const [deleting, setDeleting] = useState(false);

  const audienceFilterOptions = useMemo(() => {
    if (isAdmin) return ALL_AUDIENCES;
    const opts = [];
    if (isOwner) opts.push("Owner");
    if (isTenant) opts.push("Tenant");
    if (isActiveExecutive) opts.push("Executive");
    return [...new Set(opts)];
  }, [isAdmin, isOwner, isTenant, isActiveExecutive]);

  const canCreate = isAdmin || isActiveExecutive;

  useEffect(() => {
    if (!API_BASE_URL || !userId || isAdmin || isTenant) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.post(API_BASE_URL, {
          action: "getProfile",
          userId,
          userid: userId
        });
        const data = response?.data;
        const profile = data?.profile || data?.user || data?.data || data;
        const exec =
          String(profile?.executiveStatus || "").trim().toLowerCase() === "active";
        if (!cancelled) setIsActiveExecutive(exec);
      } catch {
        if (!cancelled) setIsActiveExecutive(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE_URL, userId, isAdmin, isTenant]);

  useEffect(() => {
    if (audience && !audienceFilterOptions.includes(audience)) {
      setAudience("");
      setPage(1);
    }
  }, [audience, audienceFilterOptions]);

  const fetchList = useCallback(async () => {
    if (!API_BASE_URL) { toast.error("Missing REACT_APP_API_URL in environment"); return; }
    if (!userId) { toast.error("Missing user id. Please login again."); return; }

    let didIncrement = false;
    try {
      const payload = {
        action: "eVoting.getAll",
        userid: userId,
        page,
        limit,
        sortBy,
        sortOrder
      };
      const q = String(search || "").trim();
      if (q) payload.search = q;
      if (audience) payload.audience = audience;
      payload.status = status || "all";

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, payload);
      const data = response?.data;

      if (isApiSuccess(data)) {
        const list = pickPollList(data);
        setItems(Array.isArray(list) ? list : []);
        const pag = data.pagination && typeof data.pagination === "object" ? data.pagination : {};
        setPagination({
          page: Number(pag.page) || page,
          limit: Number(pag.limit) || limit,
          total: Number(pag.total) || (Array.isArray(list) ? list.length : 0),
          pages: Math.max(1, Number(pag.pages) || 1)
        });
      } else {
        toast.error(data?.message || data?.error || "Failed to load polls");
        setItems([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
      setItems([]);
    } finally {
      if (didIncrement) loaderDecrement();
    }
  }, [API_BASE_URL, userId, page, limit, search, audience, status, sortBy, sortOrder]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── WebSocket: live poll updates ──────────────────────────────────────────
  useEffect(() => {
    if (!userId || !WS_URL) return undefined;

    connectWs({ baseUrl: WS_URL, userId, userType: userRole }).catch(() => {});

    const audienceApplies = (aud) => {
      const a = String(aud || "All").trim() || "All";
      if (a === "All") return true;
      if (isAdmin) return true;
      if (a === "Owner") return isOwner;
      if (a === "Tenant") return isTenant;
      if (a === "Executive") return Boolean(isActiveExecutive);
      return false;
    };

    const rowFromPoll = (p) => {
      if (!p || typeof p !== "object") return null;
      const id = String(p._id || p.id || p.eVotingId || p.pollId || "").trim();
      if (!id) return null;
      return {
        _id: id, id, pollId: id, eVotingId: id,
        question: p.question || "",
        audience: p.audience || "All",
        status: p.status || "active",
        startTime: p.startTime || null,
        setStartTime: Boolean(p.setStartTime),
        endTime: p.endTime || null,
        setEndTime: Boolean(p.setEndTime),
        allowMultipleAnswers: Boolean(p.allowMultipleAnswers),
        options: Array.isArray(p.options) ? p.options : [],
        optionCounts: Array.isArray(p.optionCounts) ? p.optionCounts : [],
        voteCount: p.voteCount != null ? p.voteCount : 0,
        createdAt: p.createdAt || new Date().toISOString(),
        createdBy: p.createdBy || null,
        adminId: p.adminId || null
      };
    };

    const unsubscribe = addWsListener((data) => {
      if (!data || typeof data !== "object") return;
      const type = String(data.type || "").toLowerCase();
      const action = String(data.action || "").toLowerCase();

      const notification =
        data?.notification && typeof data.notification === "object" ? data.notification : null;
      const notifType = String(notification?.type || "").toLowerCase();
      const isCreatePush =
        type === "poll_created" || action === "poll_created" || action === "create_poll" ||
        notifType === "poll-created" || notifType === "poll_created" ||
        (type === "notification" &&
          (action === "poll_created" || action === "create_poll" ||
            data.eVotingId || data.pollId || data.poll));

      if (isCreatePush) {
        const pushPoll =
          (data?.poll && typeof data.poll === "object" ? data.poll : null) ||
          (notification?.data?.poll && typeof notification.data.poll === "object"
            ? notification.data.poll : null) ||
          (notification?.poll && typeof notification.poll === "object"
            ? notification.poll : null) || {};

        const incomingId = String(
          pushPoll._id || pushPoll.id || pushPoll.eVotingId || pushPoll.pollId ||
          notification?.data?.pollId || data.eVotingId || data.pollId || ""
        ).trim();
        if (!incomingId) return;

        const pushAudience = pushPoll.audience || notification?.data?.audience || "All";
        if (!audienceApplies(pushAudience)) return;

        const exists = itemsRef.current.some(
          (row) => String(row._id || row.id || row.pollId || "").trim() === incomingId
        );
        if (exists) return;

      const row = rowFromPoll({
        ...pushPoll,
        _id: pushPoll._id || pushPoll.id || pushPoll.eVotingId || incomingId,
        question:
          pushPoll.question || notification?.data?.pollQuestion || notification?.message || "",
        audience: pushAudience
      });
      if (!row) return;
      setItems((prev) => [row, ...prev]);
        setPagination((pg) => ({
          ...pg,
          total: (pg.total || 0) + 1,
          pages: Math.max(1, Math.ceil(((pg.total || 0) + 1) / pg.limit))
        }));
        toast.info("New poll: " + (row.question || ""));
        return;
      }

      const isVotePush = type === "poll_vote" || action === "vote_poll";
      const isEndPush = type === "poll_ended" || action === "end_poll";
      const isStartPush = type === "poll_started" || action === "poll_started";
      if (!isVotePush && !isEndPush && !isStartPush) return;

      const pushPoll = data?.poll && typeof data.poll === "object" ? data.poll : null;
      const incomingId = String(
        data.eVotingId || pushPoll?.eVotingId || pushPoll?.id || pushPoll?._id || data.pollId || ""
      ).trim();
      if (!incomingId) return;

      setItems((prev) =>
        prev.map((row) => {
          const rowId = String(row?._id || row?.id || row?.pollId || "").trim();
          if (rowId !== incomingId) return row;
          if (isEndPush) return { ...row, status: "ended" };
          if (isStartPush) return { ...row, status: "active" };
          return {
            ...row,
            voteCount:
              pushPoll?.voteCount != null
                ? pushPoll.voteCount
                : row.voteCount != null ? row.voteCount + 1 : row.voteCount,
            optionCounts: Array.isArray(pushPoll?.optionCounts)
              ? pushPoll.optionCounts : row.optionCounts
          };
        })
      );
    });

    return unsubscribe;
  }, [userId, userRole, WS_URL, isAdmin, isOwner, isTenant, isActiveExecutive]);

  // ── Delete handlers ───────────────────────────────────────────────────────
  const confirmDelete = (row) => {
    const id = getPollId(row);
    if (!id) return;
    setDeleteTarget({ id, question: row?.question || "this poll" });
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    if (!API_BASE_URL) { toast.error("Missing REACT_APP_API_URL in environment"); return; }
    if (!userId) { toast.error("Missing user id. Please login again."); return; }

    setDeleting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, {
        action: "eVoting.delete",
        userid: userId,
        eVotingId: deleteTarget.id
      });
      const data = response?.data;
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to delete poll");
        return;
      }
      toast.success(data?.message || "Poll deleted successfully");
      const deletedId = deleteTarget.id;
      setDeleteTarget(null);
      setItems((prev) => prev.filter((row) => getPollId(row) !== deletedId));
      setPagination((pg) => ({
        ...pg,
        total: Math.max(0, (pg.total || 0) - 1),
        pages: Math.max(1, Math.ceil(Math.max(0, (pg.total || 0) - 1) / pg.limit))
      }));
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete poll");
    } finally {
      if (didIncrement) loaderDecrement();
      setDeleting(false);
    }
  };

  // ── Pagination / sort helpers ─────────────────────────────────────────────
  const applySearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const pageNum = Number(pagination.page) || page;
  const pageLimit = Number(pagination.limit) || limit;
  const totalPages = Math.max(1, Number(pagination.pages) || 1);
  const currentPage = Math.min(Math.max(1, pageNum), totalPages);

  const pageButtons = useMemo(() => {
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    const buttons = [];
    for (let p = start; p <= end; p += 1) buttons.push(p);
    return buttons;
  }, [currentPage, totalPages]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const sortIcon = (field) => {
    if (sortBy !== field) return "fa-sort";
    return sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down";
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="employee-form">
        <div className="content-header">
          <div
            className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
            style={{ gap: 12 }}
          >
            <div>
              <h1 className="m-0 txt_lft">E-Voting</h1>
            </div>
            {canCreate ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/e-voting-create")}
              >
                <i className="fas fa-plus mr-1" aria-hidden />
                Create poll
              </button>
            ) : null}
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-body">
                <div className="role-table-box">
                  {/* ── Filters ── */}
                  <div
                    className="role-table-top d-flex flex-wrap align-items-end justify-content-start"
                    style={{ gap: 12 }}
                  >
                    <form
                      onSubmit={applySearch}
                      className="role-table-search-wrap"
                      style={{ flex: "1 1 220px", maxWidth: 360 }}
                    >
                      <div className="form-group mb-0">
                        <label className="form-label" style={{ fontSize: 12 }}>
                          Search
                        </label>
                        <input
                          type="text"
                          className="form-control role-table-search"
                          placeholder="Search question…"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                        />
                      </div>
                    </form>

                    <div className="form-group mb-0" style={{ width: 160 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>Audience</label>
                      <select
                        className="form-control"
                        value={audience}
                        onChange={(e) => { setPage(1); setAudience(e.target.value); }}
                      >
                        <option value="">All audiences</option>
                        {audienceFilterOptions.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group mb-0" style={{ width: 150 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>Status</label>
                      <select
                        className="form-control"
                        value={status}
                        onChange={(e) => { setPage(1); setStatus(e.target.value); }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value || "all"} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>


                  </div>

                  {/* ── Table ── */}
                  <div className="table-responsive">
                    <table className="table table-hover role-table">
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>
                            <span className="role-th">Sl No.</span>
                          </th>
                          <th>
                            <button
                              type="button"
                              className="btn btn-link p-0 role-th"
                              onClick={() => toggleSort("question")}
                            >
                              Question{" "}
                              <i className={`fas ${sortIcon("question")}`} aria-hidden />
                            </button>
                          </th>
                          <th style={{ width: 130 }}>
                            <button
                              type="button"
                              className="btn btn-link p-0 role-th"
                              onClick={() => toggleSort("audience")}
                            >
                              Audience{" "}
                              <i className={`fas ${sortIcon("audience")}`} aria-hidden />
                            </button>
                          </th>
                          <th style={{ width: 110 }}>
                            <span className="role-th">Status</span>
                          </th>
                          <th style={{ width: 90 }}>
                            <span className="role-th">Options</span>
                          </th>
                          <th style={{ width: 150 }}>
                            <button
                              type="button"
                              className="btn btn-link p-0 role-th"
                              onClick={() => toggleSort("createdAt")}
                            >
                              Created{" "}
                              <i className={`fas ${sortIcon("createdAt")}`} aria-hidden />
                            </button>
                          </th>
                          <th style={{ width: 150 }}>
                            <button
                              type="button"
                              className="btn btn-link p-0 role-th"
                              onClick={() => toggleSort("endTime")}
                            >
                              End time{" "}
                              <i className={`fas ${sortIcon("endTime")}`} aria-hidden />
                            </button>
                          </th>
                          <th style={{ width: 130 }}>
                            <span className="role-th">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length ? (
                          items.map((row, idx) => {
                            const id = getPollId(row);
                            const pollStatus = displayPollStatus(row?.status, row?.endTime, row?.startTime);
                            const pollAudience =
                              String(row?.audience || "All").trim() || "All";
                            const optionCount = Array.isArray(row?.options)
                              ? row.options.length
                              : Number(row?.optionCount) || 0;

                            return (
                              <tr key={id || `poll-${idx}`}>
                                <td>{(currentPage - 1) * pageLimit + idx + 1}</td>
                                <td>
                                  <div>{row?.question || "—"}</div>
                                  {row?.allowMultipleAnswers ? (
                                    <small className="text-muted">Multiple answers</small>
                                  ) : null}
                                </td>
                                <td>
                                  <span
                                    className="badge"
                                    style={{
                                      padding: "0.35rem 0.65rem",
                                      borderRadius: 999,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      ...audienceBadgeStyle(pollAudience)
                                    }}
                                  >
                                    {pollAudience.toUpperCase()}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className="badge"
                                    style={{
                                      padding: "0.35rem 0.65rem",
                                      borderRadius: 999,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      ...statusBadgeStyle(pollStatus)
                                    }}
                                  >
                                    {statusLabel(pollStatus, row?.startTime)}
                                  </span>

                                </td>
                                <td>{optionCount || "—"}</td>
                                <td>{formatDateTime(row?.createdAt)}</td>
                                <td>
                                  {row?.setEndTime || row?.endTime
                                    ? formatDateTime(row?.endTime)
                                    : "—"}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-view"
                                    title="View"
                                    disabled={!id}
                                    onClick={() => navigate(`/e-voting-view/${id}`)}
                                  >
                                    <i className="fa-solid fa-eye" aria-hidden="true" />
                                  </button>
                                  {canCreate ? (
                                    <>
                                      <button
                                        type="button"
                                        className="role-action-btn role-action-edit"
                                        title="Edit"
                                        disabled={!id}
                                        onClick={() => {
                                          if (pollStatus !== "scheduled") {
                                            toast.error("Poll can't be edited after it has started");
                                            return;
                                          }
                                          navigate(`/e-voting-edit/${id}`);
                                        }}
                                      >
                                        <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                                      </button>
                                      <button
                                        type="button"
                                        className="role-action-btn role-action-delete"
                                        title="Delete"
                                        disabled={!id}
                                        onClick={() => {
                                          if (pollStatus !== "scheduled") {
                                            toast.error("Poll can't be deleted after it has started");
                                            return;
                                          }
                                          confirmDelete(row);
                                        }}
                                      >
                                        <i className="fa-solid fa-trash" aria-hidden="true" />
                                      </button>
                                    </>
                                  ) : null}                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center text-muted">
                              {search || audience || status
                                ? "No polls found"
                                : "No polls yet"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Pagination ── */}
                  <div
                    className="role-table-footer d-flex justify-content-between align-items-center flex-wrap"
                    style={{ gap: 10 }}
                  >
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      <span className="text-muted" style={{ fontSize: 12 }}>Show:</span>
                      <select
                        className="form-control"
                        style={{ width: 90 }}
                        value={limit}
                        onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Total: {pagination.total || 0}
                      </span>
                    </div>

                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={currentPage <= 1}
                        onClick={() => setPage(1)}
                      >
                        &laquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={currentPage <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        &lsaquo;
                      </button>
                      {pageButtons.map((p) => (
                        <button
                          key={p}
                          type="button"
                          className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={currentPage >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        &rsaquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={currentPage >= totalPages}
                        onClick={() => setPage(totalPages)}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Delete confirmation modal ─────────────────────────────────────── */}
      {deleteTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={() => { if (!deleting) setDeleteTarget(null); }}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(440px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">Delete poll</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="card-body">
              Are you sure you want to delete{" "}
              <strong>&quot;{deleteTarget.question}&quot;</strong>?{" "}
              This action cannot be undone. Only polls with no votes can be deleted.
            </div>
            <div
              className="card-footer d-flex justify-content-end"
              style={{ gap: 10 }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default EVoting;
